import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { XMarkIcon, UserGroupIcon, PlusIcon, TrashIcon, Cog6ToothIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import alertApi from '../../services/api/alert.api';
import userApi from '../../services/api/user.api';
import applicationApi from '../../services/api/application.api';
import crmIntegrationApi from '../../services/api/crm-integration.api';
import CrmIntegrationSetup from './CrmIntegrationSetup';

interface AlertFormModalProps {
  alertRule?: any;
  onClose: () => void;
}

interface Condition {
  id: string;
  metric: string;
  operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne';
  threshold: number;
  timeWindow: number;
}

export default function AlertFormModal({ alertRule, onClose }: AlertFormModalProps) {
  const { t } = useTranslation('alerts');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [logicalOperator, setLogicalOperator] = useState<'AND' | 'OR'>('AND');

  const [showCrmSetup, setShowCrmSetup] = useState(false);
  const [crmConfigured, setCrmConfigured] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    severity: 'warning',
    status: 'active',
    application_id: '',
    recipients: [] as string[],
    email_enabled: true,
    panel_enabled: true,
    crm_enabled: false,
    crm_whatsapp: false,
    crm_telegram: false,
    crm_call: false,
    crm_discord: false,
  });

  useEffect(() => {
    loadData();
    loadCrmStatus();
    if (alertRule) {
      const crmChannels = alertRule.notification_channels?.crm;
      setFormData({
        name: alertRule.name || '',
        description: alertRule.description || '',
        severity: alertRule.severity || 'warning',
        status: alertRule.status || 'active',
        application_id: alertRule.application_id || '',
        recipients: alertRule.recipients?.map((r: any) => r.user_id) || [],
        email_enabled: alertRule.recipients?.[0]?.notification_channels?.email !== false,
        panel_enabled: alertRule.recipients?.[0]?.notification_channels?.panel !== false,
        crm_enabled: crmChannels?.enabled || false,
        crm_whatsapp: crmChannels?.whatsapp || false,
        crm_telegram: crmChannels?.telegram || false,
        crm_call: crmChannels?.call || false,
        crm_discord: crmChannels?.discord || false,
      });

      // Load existing conditions
      if (alertRule.conditions) {
        if (alertRule.conditions.operator) {
          setLogicalOperator(alertRule.conditions.operator);
        }
        if (alertRule.conditions.conditions && Array.isArray(alertRule.conditions.conditions)) {
          setConditions(alertRule.conditions.conditions.map((c: any, i: number) => ({
            ...c,
            id: `cond-${i}`,
          })));
        }
      }
    }
  }, [alertRule]);

  const loadCrmStatus = async () => {
    try {
      const res = await crmIntegrationApi.get();
      setCrmConfigured(res.success && res.data !== null && res.data.is_active === true);
    } catch {
      setCrmConfigured(false);
    }
  };

  const loadData = async () => {
    try {
      const [usersRes, appsRes] = await Promise.all([
        userApi.getAll(10000, 0),
        applicationApi.getAll(),
      ]);

      if (usersRes.success) setUsers(usersRes.data);
      if (appsRes.success) setApplications(appsRes.data);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const addCondition = () => {
    const newCondition: Condition = {
      id: `cond-${Date.now()}`,
      metric: 'cpu_usage',
      operator: 'gt',
      threshold: 80,
      timeWindow: 300,
    };
    setConditions([...conditions, newCondition]);
  };

  const removeCondition = (id: string) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const updateCondition = (id: string, field: keyof Condition, value: any) => {
    setConditions(conditions.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (conditions.length === 0) {
      toast.error(t('form.noConditions'));
      return;
    }

    if (formData.recipients.length === 0) {
      toast.error(t('messages.selectAtLeastOneRecipient'));
      return;
    }

    if (!formData.email_enabled && !formData.panel_enabled) {
      toast.error(t('messages.selectNotificationChannel'));
      return;
    }

    try {
      setLoading(true);

      const notificationChannels: any = {
        email: formData.email_enabled,
        panel: formData.panel_enabled,
      };

      if (formData.crm_enabled && crmConfigured) {
        notificationChannels.crm = {
          enabled: true,
          whatsapp: formData.crm_whatsapp,
          telegram: formData.crm_telegram,
          call: formData.crm_call,
          discord: formData.crm_discord,
        };
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        alertType: 'threshold',
        severity: formData.severity,
        status: formData.status,
        applicationId: formData.application_id || undefined,
        notificationChannels,
        conditions: {
          operator: logicalOperator,
          conditions: conditions.map(c => ({
            metric: c.metric,
            operator: c.operator,
            threshold: c.threshold,
            timeWindow: c.timeWindow,
          })),
        },
        recipients: formData.recipients.map(userId => ({
          user_id: userId,
          notification_channels: {
            email: formData.email_enabled,
            panel: formData.panel_enabled,
          },
        })),
      };

      if (alertRule) {
        await alertApi.update(alertRule.id, payload);
      } else {
        await alertApi.create(payload);
      }

      toast.success(alertRule ? t('messages.alertUpdated') : t('messages.alertCreated'));
      onClose();
    } catch (error: any) {
      console.error('Error saving alert:', error);
      toast.error(error.response?.data?.error || t('messages.errorCreatingAlert'));
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipient = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.includes(userId)
        ? prev.recipients.filter(id => id !== userId)
        : [...prev.recipients, userId],
    }));
  };

  const selectAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      recipients: users.map(u => u.id),
    }));
  };

  const clearAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      recipients: [],
    }));
  };

  return (
    <>
    {showCrmSetup && (
      <CrmIntegrationSetup
        onClose={() => setShowCrmSetup(false)}
        onSaved={() => loadCrmStatus()}
      />
    )}
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {alertRule ? t('editAlert') : t('createAlert')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <div className="h-1 w-1 rounded-full bg-indigo-600"></div>
              <span>{t('form.basicInfo')}</span>
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fields.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('form.enterName')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fields.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('form.enterDescription')}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('fields.severity')} *
                  </label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="info">{t('severities.info')}</option>
                    <option value="warning">{t('severities.warning')}</option>
                    <option value="critical">{t('severities.critical')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('fields.status')} *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="active">{t('statuses.active')}</option>
                    <option value="inactive">{t('statuses.inactive')}</option>
                    <option value="paused">{t('statuses.paused')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fields.application')}
                </label>
                <select
                  value={formData.application_id}
                  onChange={(e) => setFormData({ ...formData, application_id: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="">{t('form.selectApplication')}</option>
                  {applications.map(app => (
                    <option key={app.id} value={app.id}>{app.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
              <div className="h-1 w-1 rounded-full bg-orange-600"></div>
              <span>{t('form.conditions')}</span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('form.conditionsDescription')}
            </p>

            {/* Logical Operator */}
            {conditions.length > 1 && (
              <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('form.logicalOperator')}
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="logicalOperator"
                      value="AND"
                      checked={logicalOperator === 'AND'}
                      onChange={(e) => setLogicalOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('conditions.operators.and')} - {conditions.length === 2 ? 'Ambas' : 'Todas'} as condições devem ser verdadeiras
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="logicalOperator"
                      value="OR"
                      checked={logicalOperator === 'OR'}
                      onChange={(e) => setLogicalOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('conditions.operators.or')} - Qualquer condição pode ser verdadeira
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Conditions List */}
            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div
                  key={condition.id}
                  className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold">
                        {index + 1}
                      </span>
                      {index > 0 && (
                        <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-md">
                          {logicalOperator}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCondition(condition.id)}
                      className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    {/* Metric */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Métrica
                      </label>
                      <select
                        value={condition.metric}
                        onChange={(e) => updateCondition(condition.id, 'metric', e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="cpu_usage">{t('metrics.cpu_usage')}</option>
                        <option value="memory_usage">{t('metrics.memory_usage')}</option>
                        <option value="disk_usage">{t('metrics.disk_usage')}</option>
                        <option value="disk_read">{t('metrics.disk_read')}</option>
                        <option value="disk_write">{t('metrics.disk_write')}</option>
                        <option value="network_rx">{t('metrics.network_rx')}</option>
                        <option value="network_tx">{t('metrics.network_tx')}</option>
                        <option value="response_time">{t('metrics.response_time')}</option>
                        <option value="error_rate">{t('metrics.error_rate')}</option>
                        <option value="request_rate">{t('metrics.request_rate')}</option>
                      </select>
                    </div>

                    {/* Operator */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Operador
                      </label>
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(condition.id, 'operator', e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="gt">{t('operators.gt')} {t('conditions.greaterThan')}</option>
                        <option value="gte">{t('operators.gte')} {t('conditions.greaterThanOrEqual')}</option>
                        <option value="lt">{t('operators.lt')} {t('conditions.lessThan')}</option>
                        <option value="lte">{t('operators.lte')} {t('conditions.lessThanOrEqual')}</option>
                        <option value="eq">{t('operators.eq')} {t('conditions.equals')}</option>
                        <option value="ne">{t('operators.ne')} {t('conditions.notEquals')}</option>
                      </select>
                    </div>

                    {/* Threshold */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Valor
                      </label>
                      <input
                        type="number"
                        value={condition.threshold}
                        onChange={(e) => updateCondition(condition.id, 'threshold', Number(e.target.value))}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        min="0"
                        step="0.1"
                      />
                    </div>

                    {/* Time Window */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Janela (s)
                      </label>
                      <input
                        type="number"
                        value={condition.timeWindow}
                        onChange={(e) => updateCondition(condition.id, 'timeWindow', Number(e.target.value))}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        min="60"
                        step="60"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {conditions.length === 0 && (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/30 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('form.noConditions')}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={addCondition}
              className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 text-indigo-700 dark:text-indigo-400 border-2 border-dashed border-indigo-300 dark:border-indigo-700 rounded-xl hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 transition-all"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="font-medium">{t('form.addCondition')}</span>
            </button>
          </div>

          {/* Notification Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <div className="h-1 w-1 rounded-full bg-purple-600"></div>
              <span>{t('form.notifications')}</span>
            </h3>

            {/* Notification Channels */}
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t('fields.notificationChannels')} *
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.email_enabled}
                    onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    📧 {t('fields.enableEmail')}
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.panel_enabled}
                    onChange={(e) => setFormData({ ...formData, panel_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    🔔 {t('fields.enablePanel')}
                  </span>
                </label>
              </div>
            </div>

            {/* CRM Channels */}
            <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span>{t('crm.channels.title')}</span>
                  {crmConfigured ? (
                    <span className="flex items-center space-x-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      <span>{t('crm.configured')}</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-1 text-xs text-yellow-600 dark:text-yellow-400">
                      <ExclamationCircleIcon className="h-3.5 w-3.5" />
                      <span>{t('crm.notConfigured')}</span>
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setShowCrmSetup(true)}
                  className="flex items-center space-x-1 text-xs px-3 py-1.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors font-medium"
                >
                  <Cog6ToothIcon className="h-3.5 w-3.5" />
                  <span>{t('crm.configure')}</span>
                </button>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={formData.crm_enabled}
                  onChange={(e) => setFormData({ ...formData, crm_enabled: e.target.checked })}
                  disabled={!crmConfigured}
                  className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                />
                <span className={`text-sm ${!crmConfigured ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {t('crm.title')}
                </span>
              </label>

              {formData.crm_enabled && crmConfigured && (
                <div className="ml-8 space-y-2 mt-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.crm_whatsapp}
                      onChange={(e) => setFormData({ ...formData, crm_whatsapp: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('crm.channels.enableWhatsapp')}
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.crm_telegram}
                      onChange={(e) => setFormData({ ...formData, crm_telegram: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('crm.channels.enableTelegram')}
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.crm_discord}
                      onChange={(e) => setFormData({ ...formData, crm_discord: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('crm.channels.enableDiscord', 'Enviar alertas via Discord')}
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.crm_call}
                      onChange={(e) => setFormData({ ...formData, crm_call: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('crm.channels.enableCall')}
                    </span>
                  </label>

                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                    {t('crm.phonesFromConfig')}
                  </p>
                </div>
              )}
            </div>

            {/* Recipients */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('fields.recipients')} * ({formData.recipients.length} {t('form.selectUsers')})
                </label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={selectAllUsers}
                    className="text-xs px-3 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    {t('common:buttons.add', 'Selecionar Todos')}
                  </button>
                  <button
                    type="button"
                    onClick={clearAllUsers}
                    className="text-xs px-3 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common:buttons.remove', 'Limpar')}
                  </button>
                </div>
              </div>

              <div className="border border-gray-300 dark:border-gray-600 rounded-xl p-4 bg-white dark:bg-gray-700 max-h-64 overflow-y-auto space-y-2">
                {users.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Nenhum usuário encontrado
                  </p>
                ) : (
                  users.map(user => (
                    <label
                      key={user.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                        formData.recipients.includes(user.id)
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-600 border-2 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.recipients.includes(user.id)}
                        onChange={() => toggleRecipient(user.id)}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <UserGroupIcon className="h-5 w-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>

              {formData.recipients.length === 0 && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {t('fields.noRecipientsSelected')}
                </p>
              )}
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('common:messages.loading')}</span>
                </div>
              ) : (
                t('common:buttons.save')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
